import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ArrowDownwards from "../../icons/ArrowDownwards";

const SelectionInput = ({
  title,
  options,
  placeholder,
  onChange,
  name,
  error,
  children,
  className,
  disabled,
  value,
  custom,
  arrowColor,
}) => {
  const childCount = React.Children.count(children);
  const [selectedValue, setSelectedValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, placement: "bottom" });
  const mainDivRef = useRef(null);
  const dropRef = useRef(null);
  // Matches the `max-h-[200px]` applied to the dropdown container below;
  // used to decide whether to flip the panel above the trigger when there's
  // not enough room below (e.g. cards near the bottom of the viewport).
  const DROPDOWN_MAX_HEIGHT = 200;

  useEffect(() => {
    if (placeholder || (placeholder === "" && !value))
      setSelectedValue(placeholder);
    if (!placeholder && value) setSelectedValue(value);
    if (placeholder && value) setSelectedValue(value);
  }, [placeholder, value, selectedValue]);

  const toggleDropdown = () => {
    const rect = mainDivRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // Estimate the panel's real height from the option count (~44px per
    // row, capped at the panel max). The previous code assumed a fixed
    // 200px height, so a short dropdown like the 2-option AM/PM picker
    // flipped above the field whenever it sat in the lower half of the
    // viewport — and, anchoring with that inflated 200px, rendered
    // floating ~100px ABOVE the field instead of attached to it. Sizing
    // the estimate to the actual content keeps small dropdowns docked to
    // the field and only flips them when they genuinely won't fit below.
    const ITEM_HEIGHT = 44;
    const estimatedHeight = Math.min(
      DROPDOWN_MAX_HEIGHT,
      childCount * ITEM_HEIGHT + (custom ? ITEM_HEIGHT : 0) + 4
    );
    // Flip above the trigger only when the panel won't fit below AND
    // there's more room above.
    const flipUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    // When flipping, anchor the panel's bottom to the trigger's top using
    // its estimated height so it stays attached (never floats away).
    const panelHeight = Math.min(estimatedHeight, spaceAbove);
    setPosition({
      top: flipUp
        ? rect.top + window.scrollY - panelHeight
        : rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      placement: flipUp ? "top" : "bottom",
    });
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  // const handleScroll = () => {
  //   setIsOpen(false);
  // };

  useEffect(() => {
    function handleClickOutside(event) {
      if (mainDivRef.current && !mainDivRef.current.contains(event.target)) {
        closeDropdown();
      }
    }

    function handleScroll(event) {
      // Check if the scroll event occurred inside the dropdown
      const dropdownElement = ReactDOM.findDOMNode(dropRef.current);
      if (dropdownElement && dropdownElement.contains(event.target)) {
        return;
      }

      closeDropdown();
    }

    if (isOpen) {
      document.addEventListener("scroll", handleScroll, true);
      document.addEventListener("click", handleClickOutside);
    } else {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  /** */
  const [childState, setChildState] = useState([]);
  useEffect(() => {
    // Create an array of objects representing each child and its value
    const mappedChildren = React.Children.map(children, (child) => ({
      value: child.props.value,
      label: child.props.children,
    }));
    setChildState(mappedChildren || []);
  }, [children]);

  return (
    <div className="grid grid-flow-row h-full min-h-[45px] w-full">
      {title && (
        <label className="text-darkgrey text-bodyRB mb-[2px]">{title}</label>
      )}
      <div
        onClick={toggleDropdown}
        ref={mainDivRef}
        className={`grid grid-flow-row min-w-max bg-white shadow-card  hover:cursor-pointer rounded-15  ${
          error ? "border-[1.5px]  border-danger" : "border border-[#E7EBEC]"
        } ${className}
        ${
          // Flatten the corners that meet the dropdown panel: bottom corners
          // when the panel renders below (default), top corners when the
          // panel flips above. Previously this always flattened the bottom,
          // which left a visual stair-step on flipped-up dropdowns.
          isOpen
            ? `border-[1.5px] border-[#E7EBEC] ${
                position.placement === "top"
                  ? "rounded-tl-none rounded-tr-none"
                  : "rounded-bl-none rounded-br-none"
              }`
            : ""
        }
        `}
      >
        <p className=" flex items-center justify-between pl-5 text-bodyRB text-darkgrey select-none capitalize min-w-max">
          <span>
            {selectedValue ? (
              selectedValue
            ) : value ? (
              value
            ) : (
              <span className="opacity-60 text-gray-500">{placeholder}</span>
            )}{" "}
          </span>
          {!disabled && (
            <div className=" flex items-center  pr-3 pointer-events-none">
              <ArrowDownwards color={arrowColor} />
            </div>
          )}
        </p>
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropRef}
            className={`sel-input-scrollable ${
              title ? "top-[75px]" : "top-11"
            }     bg-white shadow-card ${
              position.placement === "top"
                ? "rounded-tl-15 rounded-tr-15 border-b-0"
                : "rounded-bl-15 rounded-br-15 border-t-0"
            } border-[1.5px] border-[#E7EBEC] max-h-[240px] overflow-auto`}
            style={{
              position: "absolute",
              top: `${position.top}px`,
              left: `${position.left}px`,
              // Narrow triggers (e.g. the 100 px AM/PM picker) produce a
              // cramped popover when the panel matches the trigger width
              // 1:1 — floor it at 120 px so 2–3 character options aren't
              // crushed against the chrome.
              width: `${position.width}px`,
              minWidth: "120px",
              zIndex: 99999,
            }}
          >
            <ul className="">
              <li className="">
                {childState.map((child, index) => {
                  const isFirst = index === 0;
                  const isLast = index === childCount - 1;
                  // Corner rounding mirrors the panel's rounded edge so a
                  // selected/hover background fills cleanly into the
                  // panel corner instead of leaving a flat shoulder
                  // inside a rounded chrome. Placement-aware: when the
                  // panel flips above the trigger, the "first" item is
                  // also the corner that meets nothing and stays
                  // rounded at top; the "last" item meets the trigger
                  // below and stays square.
                  const cornerCls =
                    position.placement === "top"
                      ? isFirst
                        ? "rounded-tl-15 rounded-tr-15"
                        : ""
                      : isLast
                        ? "rounded-bl-15 rounded-br-15"
                        : "";
                  // Bottom divider on every item except the last leaves
                  // a clean fade into the panel's rounded edge instead
                  // of a phantom hairline above the corner.
                  const dividerCls = isLast ? "" : "border-b";
                  const selected =
                    selectedValue === (child?.label || child?.value);
                  return (
                    <div
                      onClick={() => {
                        setSelectedValue(child?.label);
                        closeDropdown();
                        if (onChange && typeof onChange === "function") {
                          onChange(child?.value);
                        }
                      }}
                      key={index}
                      // `min-h-10` + `py-2 whitespace-normal break-words leading-snug`
                      // lets long option labels wrap onto multiple lines
                      // instead of being clipped. Row height auto-grows.
                      className={`pl-5 pr-3 py-2 min-h-10 flex items-center capitalize text-bodyRB text-darkgrey whitespace-normal break-words leading-snug hover:text-white hover:bg-primary hover:cursor-pointer ${dividerCls} ${cornerCls} ${
                        selected
                          ? "!bg-lightgray hover:text-darkgrey hover:cursor-default"
                          : "active:bg-primary active:text-white cursor-pointer"
                      }`}
                    >
                      {child.label}
                    </div>
                  );
                })}
              </li>
              <div className={"sticky bottom-0 bg-white"}>{custom}</div>
            </ul>
          </div>,
          document.body
        )}
      {error && (
        <p className="text-danger text-smallLB mx-2 my-0.5 lg:-mb-5 -mb-1 absolute bottom-1">
          {error}
        </p>
      )}
    </div>
  );
};

export default SelectionInput;

{
  /* <select
          onChange={onChange}
          name={name}
          disabled={disabled}
          className="  p-2 appearance-none bg-transparent pl-5 text-darkgrey text-bodyRB outline-none capitalize "
        >
          <option
            value=""
            disabled
            selected
            hidden
            className="text-greyedtext opacity-10"
          >
            {placeholder}
          </option>
          {children}
        </select> */
}
